"use client"

import { FileUpload, FormField } from "@/components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSend, faXmark } from "@instadeepai/fontawesome-pro-regular"
import { Controller } from "react-hook-form"

import {
  Button,
  Input,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextArea,
} from "@shared/ui"

import { useContactForm } from "../hooks"

type ContactFormProps = {
  moduleOptions: { id: string; title: string }[]
}

export function ContactForm({ moduleOptions }: ContactFormProps) {
  const {
    files,
    progress,
    inputRef,
    control,
    handleSubmit,
    errors,
    onDrop,
    onFileChange,
    handleUploadClick,
    removeFile,
    onSubmit,
  } = useContactForm()

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-1 flex-col gap-4 pr-9 text-gray-700 max-h-[90vh] overflow-auto"
    >
      <FormField label="Module">
        <Controller
          name="module"
          control={control}
          render={({ field }: { field: any }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a module" />
              </SelectTrigger>
              <SelectContent>
                {moduleOptions.map((mod) => (
                  <SelectItem key={mod.id} value={mod.id}>
                    {mod.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors.module && (
          <p className="mt-1 text-sm text-red-500">{errors.module.message}</p>
        )}
      </FormField>
      <FormField label="Email Address">
        <Controller
          name="email"
          control={control}
          render={({ field }: { field: any }) => (
            <Input
              {...field}
              placeholder="Email Address"
              type="email"
              error={!!errors.email}
              value={field.value || ""}
            />
          )}
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
        )}
      </FormField>
      <FormField label="Send a Message">
        <Controller
          name="message"
          control={control}
          render={({ field }: { field: any }) => (
            <TextArea
              {...field}
              placeholder="Create a message"
              className="min-h-[120px] w-full rounded-md border-[#E4E4E4]"
              invalid={!!errors.message}
            />
          )}
        />
        {errors.message && (
          <p className="mt-1 text-sm text-red-500">{errors.message.message}</p>
        )}
      </FormField>
      <div className="flex flex-col gap-6">
        <FileUpload
          inputRef={inputRef as React.RefObject<HTMLInputElement>}
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={handleUploadClick}
          onChange={onFileChange}
        />
        <div className="space-y-2 overflow-auto max-h-28">
          {files.map((file, idx) => (
            <div key={file.name + idx} className="flex items-center gap-2">
              <span className="text-sm text-gray-700 truncate flex-1">
                {file.name}
              </span>
              <Progress
                value={progress[file.name] || 0}
                className="mx-2 flex-1"
              />
              <span className="w-10 text-right text-sm text-gray-700">
                {Math.round(progress[file.name] || 0)}%
              </span>
              <button
                type="button"
                onClick={() => removeFile(file.name)}
                className="ml-2 p-1 text-red-500 hover:text-red-700 transition-colors"
                title="Remove file"
              >
                <FontAwesomeIcon icon={faXmark} className="text-md" />
              </button>
            </div>
          ))}
        </div>
      </div>
      <Button
        type="submit"
        className="w-full rounded-xl bg-primary-700 font-semibold text-white transition hover:bg-primary-800 lg:w-48 flex-shrink-0"
        rightIcon={<FontAwesomeIcon icon={faSend} />}
      >
        Send Message
      </Button>
    </form>
  )
}
